import { ActionEffectivenessCompliance } from 'frontcore/presentation/actions/domain/types'
import { ComputedRef, Ref } from 'vue'
import { DropDownMenuItemProps } from 'frontcore/views/drop-down/domain/types'
import { ImprovementIdea } from 'frontcore/domain/improvement-ideas/types'
import { Nullable } from "frontcore/presentation/types"
import { Priority, ProblemType } from 'frontcore/presentation/problems/domain/types'
import { SecondaryButtonArgs } from 'frontcore/views/buttons/button-secondary/types'
import { SegmentedButtonItem } from 'frontcore/views/segmented-button/types'
import { StatusBadgeInfo } from "frontcore/views/badges/status-badge/statusBadge.types"

export type ImprovementDriverEscalation = {
  id: number
  escalatedBy: Nullable<string>
  timesEscalated: Nullable<number>
  visibleOnShopFloorManagement: boolean
}

export type ImprovementDriverItem = {
  id: number
  categoryName?: string
  date: string
  effectivenessComplianceId?: ActionEffectivenessCompliance
  name: string
  personName: string
  status: StatusBadgeInfo
  routeName: string
  isExpired: boolean
  escalation: Nullable<ImprovementDriverEscalation>
  button?: SecondaryButtonArgs
}

export type ImprovementDriverProblem = {
  perimeter?:  number | null
  priority: number | null
  problem_type: number | null
  pilot: number | null
  supervisor: number | null
  description: string
  participants: number[]
  categories: number[]
  occurrence_date: Nullable<Date>
  close_due_date: Nullable<Date>
  presentation_date: Nullable<Date>
}

export type ImprovementDriverAction = {
  what: string,
  person: number,
  when: Date,
}

export type ImprovementDriverIdea = Pick<ImprovementIdea,
  'currentSituation'
  | 'proposal'
  | 'emitterId'
  | 'pilotId'
  | 'validatorId'
  | 'perimeterId'
  | 'participantIds'
  | 'categoryIds'
  | 'dueDate'
  | 'evidenceIds' >

export type ImprovementDriverHSERisk = {
  description: string
  due_date?: Date
  category: number
  priority: number
  pilot: number
  perimeter?: number
  created_by?: number
  criterion_in_audit?: number
  initial_state_evidence: number[]
}

type ImprovementDriverStatusAPIResponse = {
  name: string,
  color_class: string,
  background_color_class?: string
  circle_custom_color?: string
}

type ImprovementDriverEscalationAPIResponse = {
  escalation_id: number
  escalated_by: Nullable<string>
  times_escalated: Nullable<number>
  visible_on_shop_floor_management: boolean
}

export type ImprovementDriverItemAPIResponse = {
  id: number
  category_name?: string
  date: string
  effectiveness_compliance?: number
  escalation: Nullable<ImprovementDriverEscalationAPIResponse>
  is_expired: boolean
  name: string
  person_name: string
  route_name: string
  status: ImprovementDriverStatusAPIResponse
}

export interface ImprovementDriverProblemUseCase {
  arePrioritiesEnabled: ComputedRef<boolean>
  priorities: Priority[]
  isSupervisorEnabled: ComputedRef<boolean>
  pilots: ComputedRef<DropDownMenuItemProps[]>
  problemTypesAvailable: ComputedRef<ProblemType[]>
  supervisors: ComputedRef<DropDownMenuItemProps[]>
  participants: ComputedRef<DropDownMenuItemProps[]>
  categories: ComputedRef<DropDownMenuItemProps[]>
  isDataLoaded: Ref<boolean>
  collapsed: Ref<boolean>
  perimeters: ComputedRef<DropDownMenuItemProps[]>
  loadCategoriesSelector: () => void
  loadPeopleSelectors: () => void
  toggleCollapse: () => void
}

export interface ImprovementDriverModalUseCase {
  currentlyOpenTab: Ref<number>
  improvementDriverIsValid: ComputedRef<boolean>
  defaultProblemData: ImprovementDriverProblem,
  problem: Ref<ImprovementDriverProblem>
  action: Ref<ImprovementDriverAction|null>
  actionIsValid: Ref<boolean>
  problemIsValid: Ref<boolean>
  problemTypesAvailable: ComputedRef<{ id: number; value: boolean; }[]>
  segmentedButtonOptions: ComputedRef<SegmentedButtonItem[]>
  idea: Ref<ImprovementDriverIdea | null>
  ideaIsValid: Ref<boolean>
  hseRisk: Ref<ImprovementDriverHSERisk | null>
  hseRiskIsValid: Ref<boolean>
  changeTab: (tab: ImprovementDriversModalTabID) => void
}

export type Person = {
  id: number
  name: string
  active: boolean
  is_operator: boolean
  is_problem_supervisor: boolean
  user: number
}

export enum ImprovementDriversModalTabID  {
  Action = 1,
  Idea,
  Problem,
  HSERisk,
}

export type ImprovementDriverListModel = {
  count: number
  items: ImprovementDriverItem[]
}

export class ImprovementDriverListAPIServiceError extends Error {
}

export interface ImprovementDriverListAPIService {
  fetch: (queryParams: Record<string, unknown>, url: string) => Promise<ImprovementDriverListModel>
}
